<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import { ReservationsService } from '@/api/services/reservations.service';
  import V2ReservationDateOverrideModal from '@/components/modals/reservations/V2ReservationDateOverrideModal.vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import PosUiSettingsCard from '@/components/pos-v2/ui/PosUiSettingsCard.vue';
  import { useModal } from '@/composition/use-modal';
  import { useNow } from '@vueuse/core';
  import { useReservationDateOverrides } from '@/composition/reservations/use-reservation-date-overrides';
  import { useStableWindowSize } from '@/composition/use-stable-window-size';
  import { useUi } from '@/composition/use-ui';
  import {
    formatDateLong,
    getReservationTimeZone,
    toDateKey,
  } from '@/domain/reservations/reservation-time';
  import { AlertType } from '@/interfaces/IAlert';
  import type {
    IReservationDateOverride,
    IReservationSettingsConfiguration,
  } from '@/interfaces/IReservationSettings';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  const props = defineProps<{
    configuration: IReservationSettingsConfiguration;
  }>();
  const emit = defineEmits<{ (event: 'saved'): void }>();
  const proxy = getCurrentInstance()!.proxy;
  const modals = useModal();
  const ui = useUi();
  const store = useRestaurantStore();
  const { width } = useStableWindowSize();
  const zone = computed(() =>
    getReservationTimeZone(
      store.selectedBranch?.location,
      store.restaurant?.country
    )
  );
  const now = useNow({ interval: 60000 });
  const today = computed(() => toDateKey(now.value, zone.value));
  const dates = useReservationDateOverrides(
    () => props.configuration,
    () => today.value
  );
  const {
    upcoming,
    past,
    newestFetched,
    oldestFetched,
    pastOpen,
    loadingFuture,
    loadingPast,
    futureError,
    pastError,
    loadFuture,
    loadPast,
    togglePast,
  } = dates;
  const deleting = ref<number | null>(null);
  const error = ref('');
  let alive = true;
  onBeforeUnmount(() => {
    alive = false;
  });
  const tr = (key: string) => String(proxy.$t(key));
  const groups = computed(() => [
    { id: 'upcoming', rows: upcoming.value },
    ...(pastOpen.value ? [{ id: 'past', rows: past.value }] : []),
  ]);
  watch(
    () =>
      `${props.configuration.settings.restaurantId}:${props.configuration.settings.branchId}`,
    () => {
      deleting.value = null;
      error.value = '';
    }
  );
  function saved(row: IReservationDateOverride) {
    if (!alive) return;
    dates.saved(row);
    emit('saved');
  }
  function edit(override?: IReservationDateOverride) {
    modals.show(
      V2ReservationDateOverrideModal,
      { configuration: props.configuration, override },
      {
        name: 'v2-reservation-date-override',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        width: 520,
        maxWidth: Math.round(width.value * 0.95),
        clickToClose: false,
      },
      { saved }
    );
  }
  /** The interval only matters for custom windows on the grid (not closed days, not fixed times). */
  function hasGridWindow(row: IReservationDateOverride): boolean {
    return !row.closed && !!row.windows?.some((window) => !window.arrivalTimes);
  }
  function label(date: string | null) {
    return date ? formatDateLong(date, zone.value, proxy.$i18n.locale) : '';
  }
  async function remove(row: IReservationDateOverride) {
    if (deleting.value !== null) return;
    const configuration = props.configuration;
    const accepted = await modals.confirm({
      title: tr('reservations.dates.deleteTitle'),
      message: label(row.date),
      acceptText: tr('delete'),
      acceptColor: 'error',
    });
    if (
      !accepted ||
      !alive ||
      props.configuration.settings.restaurantId !==
        configuration.settings.restaurantId ||
      props.configuration.settings.branchId !==
        configuration.settings.branchId ||
      store.restaurant?.id !== configuration.settings.restaurantId ||
      store.selectedBranch?.id !== configuration.settings.branchId
    )
      return;
    deleting.value = row.id;
    error.value = '';
    try {
      await ReservationsService.deleteOverride(row.id);
      if (
        !alive ||
        props.configuration.settings.restaurantId !==
          configuration.settings.restaurantId ||
        props.configuration.settings.branchId !==
          configuration.settings.branchId ||
        store.restaurant?.id !== configuration.settings.restaurantId ||
        store.selectedBranch?.id !== configuration.settings.branchId
      )
        return;
      deleting.value = null;
      ui.showToast(AlertType.Success, tr('reservations.dates.deleted'));
      dates.removed(row);
      emit('saved');
    } catch (cause) {
      if (
        !alive ||
        props.configuration.settings.restaurantId !==
          configuration.settings.restaurantId ||
        props.configuration.settings.branchId !==
          configuration.settings.branchId ||
        store.restaurant?.id !== configuration.settings.restaurantId ||
        store.selectedBranch?.id !== configuration.settings.branchId
      )
        return;
      deleting.value = null;
      error.value = tr('reservations.dates.error');
      ui.serverError(cause, error.value);
    }
  }
</script>

<template>
  <PosUiSettingsCard
    class="reservation-dates-settings"
    flush
    :title="$t('reservations.settings.nav.dates')"
    :description="$t('reservations.dates.description')"
  >
    <template #actions>
      <PosUiButton
        color="neutral"
        variant="outline"
        icon="plus"
        @click="edit()"
        >{{ $t('reservations.dates.add') }}</PosUiButton
      >
    </template>
    <p v-if="error" class="list-error" role="alert">{{ error }}</p>
    <div v-for="group in groups" :key="group.id" class="date-list">
      <h3 class="group-heading">
        {{
          $t(
            group.id === 'past'
              ? 'reservations.dates.past'
              : 'reservations.dates.upcoming'
          )
        }}
        <span
          v-if="group.id === 'past' ? oldestFetched : newestFetched"
          class="range"
        >
          {{ label(group.id === 'past' ? oldestFetched : today) }} –
          {{ label(group.id === 'past' ? today : newestFetched) }}
        </span>
      </h3>
      <PosUiEmptyState
        v-if="
          !group.rows.length &&
          (group.id === 'past'
            ? !loadingPast && !pastError
            : !loadingFuture && !futureError)
        "
        icon="calendar-outline"
        :title="$t('reservations.dates.emptyTitle')"
        :description="
          group.id === 'upcoming'
            ? $t('reservations.dates.description')
            : undefined
        "
      />
      <article v-for="row in group.rows" :key="row.id" class="date-card">
        <div class="date-details">
          <h3>{{ label(row.date) }}</h3>
          <div class="badges">
            <PosUiBadge v-if="row.depositRequired === true" color="info">{{
              $t('reservations.settings.nav.deposit')
            }}</PosUiBadge>
            <PosUiBadge v-if="row.closed" color="error">{{
              $t('reservations.dates.closed')
            }}</PosUiBadge
            ><PosUiBadge
              v-else-if="row.windows && row.windows.length"
              color="info"
              >{{ $t('reservations.dates.customHours') }}</PosUiBadge
            ><PosUiBadge v-else>{{
              $t('reservations.dates.form.mode.open')
            }}</PosUiBadge
            ><PosUiBadge v-if="row.onlineClosed" color="warning">{{
              $t('reservations.dates.onlineClosed')
            }}</PosUiBadge>
          </div>
          <p v-if="row.windows && !row.closed" class="hours">
            <span v-for="(window, index) in row.windows" :key="index"
              >{{ window.from.slice(0, 5) }} – {{ window.to.slice(0, 5)
              }}<span v-if="window.to < window.from">
                · {{ $t('reservations.shifts.crossesMidnight') }}</span
              ><span v-if="window.maxCovers">
                ·
                {{
                  $t('reservations.shifts.maxCovers', {
                    count: window.maxCovers,
                  })
                }}</span
              ></span
            >
          </p>
          <p
            v-if="row.slotIntervalMin && hasGridWindow(row)"
            class="shift-times"
          >
            {{
              $t('reservations.shifts.everyMinutes', {
                minutes: row.slotIntervalMin,
              })
            }}
          </p>
          <p v-if="row.note" class="note">{{ row.note }}</p>
        </div>
        <PosUiPopover
          placement="bottom"
          align="end"
          :disabled="deleting !== null"
          ><template #trigger="{ toggle }"
            ><PosUiButton
              color="neutral"
              variant="ghost"
              icon="dots-horizontal"
              :aria-label="$t('posV2.moreActions')"
              :disabled="deleting !== null"
              @click="toggle" /></template
          ><template #default="{ close }"
            ><div class="actions-menu">
              <PosUiButton
                color="neutral"
                variant="ghost"
                icon="pencil-outline"
                block
                @click="
                  close();
                  edit(row);
                "
                >{{ $t('reservations.detail.edit') }}</PosUiButton
              ><PosUiButton
                color="error"
                variant="ghost"
                icon="delete-outline"
                block
                @click="
                  close();
                  remove(row);
                "
                >{{ $t('delete') }}</PosUiButton
              >
            </div></template
          ></PosUiPopover
        >
      </article>
    </div>
    <div v-if="pastOpen" class="group-actions">
      <p v-if="pastError" class="error" role="alert">
        {{ $t('reservations.settings.loadError') }}
      </p>
      <PosUiButton
        color="neutral"
        variant="outline"
        :disabled="loadingPast"
        @click="loadPast()"
        >{{
          $t(
            pastError
              ? 'reservations.page.retry'
              : 'reservations.dates.loadOlder'
          )
        }}</PosUiButton
      >
    </div>
    <template #footer>
      <div class="footer-actions">
        <PosUiButton
          color="neutral"
          variant="ghost"
          :icon="pastOpen ? 'chevron-up' : 'chevron-down'"
          :aria-expanded="pastOpen"
          @click="togglePast"
          >{{ $t('reservations.dates.past') }}</PosUiButton
        >
        <div class="load-later">
          <p v-if="futureError" class="error" role="alert">
            {{ $t('reservations.settings.loadError') }}
          </p>
          <PosUiButton
            color="neutral"
            variant="ghost"
            :disabled="loadingFuture"
            @click="loadFuture()"
          >
            {{
              $t(
                futureError
                  ? 'reservations.page.retry'
                  : 'reservations.dates.loadLater'
              )
            }}
          </PosUiButton>
        </div>
      </div>
    </template>
  </PosUiSettingsCard>
</template>

<style scoped lang="scss">
  .reservation-dates-settings {
    .error {
      @apply tw-m-0 tw-text-sm tw-text-red-600;
    }
    .list-error {
      @apply tw-m-0 tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-px-5 tw-py-3 tw-text-sm tw-text-red-600 md:tw-px-6;
    }
    .group-heading {
      @apply tw-m-0 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-bg-canvas tw-px-5 tw-py-2 tw-text-xs tw-font-semibold tw-text-neutral-600 md:tw-px-6;
    }
    .date-list + .date-list .group-heading {
      @apply tw-border-t;
    }
    .range {
      @apply tw-hidden tw-font-normal tw-text-neutral-400 sm:tw-inline;
    }
    .date-card {
      @apply tw-flex tw-items-start tw-justify-between tw-gap-3 tw-px-5 tw-py-4 md:tw-px-6;
      & + .date-card {
        @apply tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200;
      }
    }
    .date-details {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-2;
      h3 {
        @apply tw-m-0 tw-text-sm tw-font-semibold tw-text-neutral-900;
      }
    }
    .badges {
      @apply tw-flex tw-flex-wrap tw-gap-1.5;
    }
    .hours {
      @apply tw-m-0 tw-flex tw-flex-col tw-gap-1 tw-text-xs tw-tabular-nums tw-text-neutral-600;
    }
    .shift-times {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
    .note {
      @apply tw-m-0 tw-break-words tw-whitespace-pre-wrap tw-text-sm tw-text-neutral-600;
    }
    .group-actions {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200 tw-px-5 tw-py-3 md:tw-px-6;
    }
    .actions-menu {
      @apply tw-grid tw-gap-1;
    }
    .footer-actions {
      @apply tw--mx-3 tw--my-2 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2;
    }
    .load-later {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;
    }
  }
</style>
